/**
 * Real-time Firestore read hooks — the SPA's single read mechanism (§3.1 / §6.1).
 *
 * Every hook subscribes via `onSnapshot`, so the UI updates live the instant a
 * backend projection write lands after an admin action. No polling, ever. All
 * reads are gated server-side by the `isPlatformAdmin()` security rule.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  limit as limitConstraint,
  onSnapshot,
  orderBy as orderByConstraint,
  query,
  where as whereConstraint,
} from "firebase/firestore";
import type { DocumentData, QueryConstraint, WhereFilterOp } from "firebase/firestore";
import { db } from "@/config/firebase";
import type { CollectionName } from "./collections";
import type { WithDocId } from "./types";

export interface ReadState<T> {
  data: T;
  loading: boolean;
  error: Error | null;
}

export interface LiveQueryState<T> extends ReadState<T> {
  fromCache: boolean;
  lastServerSnapshotAt: number | null;
}

export type Filter = [field: string, op: WhereFilterOp, value: unknown];
export type Order = [field: string, dir?: "asc" | "desc"];

export interface QuerySpec {
  filters?: Filter[];
  orderBy?: Order[];
  /** Max docs to subscribe to. Increasing this re-subscribes with a larger window. */
  limit?: number;
}

/**
 * Firestore returns date-time fields as native `Timestamp` objects ({seconds, nanoseconds}),
 * NOT ISO strings. Our types declare those fields as `string`, and rendering a raw Timestamp
 * as a React child throws ("Objects are not valid as a React child"). Recursively convert
 * every Timestamp to an ISO string (into nested maps + arrays too, e.g. `payload` /
 * `spendingPolicy`) so the data matches its types and renders everywhere. Other Firestore
 * scalar/class values (numbers, strings, GeoPoint, …) pass through untouched.
 */
function normalizeFirestore(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(normalizeFirestore);
  if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = normalizeFirestore(v);
    return out;
  }
  return value;
}

function attachDocId<T>(id: string, data: DocumentData): WithDocId<T> {
  return { ...(normalizeFirestore(data) as T), _docId: id };
}

/** Live single-document read by Firestore doc id (path read — no query analysis). */
export function useFirestoreDoc<T>(
  collectionName: CollectionName,
  id: string | null | undefined,
): ReadState<WithDocId<T> | null> & { refresh: () => void } {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const [state, setState] = useState<ReadState<WithDocId<T> | null>>({
    data: null,
    loading: !!id,
    error: null,
  });

  useEffect(() => {
    if (!id) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    const ref = doc(db, collectionName, id);
    return onSnapshot(
      ref,
      (snap) => {
        setState({
          data: snap.exists() ? attachDocId<T>(snap.id, snap.data()) : null,
          loading: false,
          error: null,
        });
      },
      (err) => setState({ data: null, loading: false, error: err }),
    );
  }, [collectionName, id, revision]);

  return { ...state, refresh };
}

/** Live collection query (filters + order + limit). Returns rows with their doc id. */
export function useFirestoreQuery<T>(
  collectionName: CollectionName,
  spec: QuerySpec = {},
  /** When false, the subscription is suspended (e.g. waiting on a dependency). */
  enabled = true,
): LiveQueryState<WithDocId<T>[]> & { refresh: () => void } {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const [state, setState] = useState<LiveQueryState<WithDocId<T>[]>>({
    data: [],
    loading: enabled,
    error: null,
    fromCache: true,
    lastServerSnapshotAt: null,
  });

  // Stable key so the effect only re-subscribes when the query actually changes.
  const specKey = JSON.stringify({ c: collectionName, s: spec, enabled, revision });

  useEffect(() => {
    if (!enabled) {
      setState({
        data: [],
        loading: false,
        error: null,
        fromCache: true,
        lastServerSnapshotAt: null,
      });
      return;
    }
    setState({ data: [], loading: true, error: null, fromCache: true, lastServerSnapshotAt: null });
    let active = true;
    const constraints: QueryConstraint[] = [];
    for (const [field, op, value] of spec.filters ?? []) {
      constraints.push(whereConstraint(field, op, value));
    }
    for (const [field, dir] of spec.orderBy ?? []) {
      constraints.push(orderByConstraint(field, dir ?? "desc"));
    }
    if (spec.limit) constraints.push(limitConstraint(spec.limit));

    const q = query(collection(db, collectionName), ...constraints);
    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (!active) return;
        setState((previous) => ({
          data: snap.docs.map((d) => attachDocId<T>(d.id, d.data())),
          loading: false,
          error: null,
          fromCache: snap.metadata.fromCache,
          lastServerSnapshotAt: snap.metadata.fromCache
            ? previous.lastServerSnapshotAt
            : Date.now(),
        }));
      },
      (err) => {
        if (active)
          setState({
            data: [],
            loading: false,
            error: err,
            fromCache: true,
            lastServerSnapshotAt: null,
          });
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey]);

  return { ...state, refresh };
}

/**
 * Paginated live query: subscribes to a growing window via `limit`. `loadMore`
 * bumps the window by `pageSize`; `hasMore` is a heuristic (we received a full
 * window, so there may be more). Good enough for ops volumes without cursor
 * juggling, and stays fully real-time.
 */
export function usePaginatedQuery<T>(
  collectionName: CollectionName,
  spec: Omit<QuerySpec, "limit"> = {},
  pageSize = 50,
  enabled = true,
): ReadState<WithDocId<T>[]> & {
  loadMore: () => void;
  hasMore: boolean;
  pageSize: number;
  refresh: () => void;
} {
  const [windowSize, setWindowSize] = useState(pageSize);
  const specRef = useRef<string>("");
  const nextSpecKey = JSON.stringify({ c: collectionName, s: spec });

  // Reset the window whenever the underlying query changes.
  if (specRef.current !== nextSpecKey) {
    specRef.current = nextSpecKey;
    if (windowSize !== pageSize) setWindowSize(pageSize);
  }

  const result = useFirestoreQuery<T>(collectionName, { ...spec, limit: windowSize }, enabled);
  const hasMore = result.data.length >= windowSize;

  return useMemo(
    () => ({
      ...result,
      hasMore,
      pageSize,
      loadMore: () => setWindowSize((w) => w + pageSize),
    }),
    [result, hasMore, pageSize],
  );
}
